import { useQueryClient } from '@tanstack/react-query'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useLatestState } from '../../../advanced-settings/hooks'
import { InstallationConfigDto, InstallationConfigPUTDto, syncInstallation, updateInstallationConfig } from '../../../installation/api'
import { installationConfigKey } from '../../../installation/hooks'
import { ChargingPriorityType } from '../pages'
import { usePortalMutation } from 'src/Libs/api'

interface SolarConsumptionPriorityProps {
  installationId: number
  installationConfig: Nullable<InstallationConfigDto>
  queryIsLoading: boolean
}

interface ReturnType {
  chargingPriority: ChargingPriorityType
  wallboxOption: Options<ChargingPriorityType>[number]
  storageOption: Options<ChargingPriorityType>[number]
  updateChargingPriority: (newPriority: ChargingPriorityType) => void
  isLoading: boolean
}

export const useSolarConsumptionPriority = ({ installationId, installationConfig, queryIsLoading }: SolarConsumptionPriorityProps): ReturnType => {
  const { t } = useCustomTranslation()
  const { enqueueErrorStack } = useSnackbar()
  const authFetch = useAuthFetchWithResult()
  const queryClient = useQueryClient()

  const wallboxOption: Options<ChargingPriorityType>[number] = {
    value: 'Wallbox First',
    label: t('user-settings.solar-page.wallbox-label'),
  }

  const storageOption: Options<ChargingPriorityType>[number] = {
    value: 'Storage first',
    label: t('user-settings.solar-page.storage-label'),
  }

  const currentChargingPriority: ChargingPriorityType = installationConfig?.emsBatteryBeforeCarMode ? 'Storage first' : 'Wallbox First'

  const [chargingPriority, setChargingPriority] = useLatestState<ChargingPriorityType>(currentChargingPriority)

  const mutateInstallationConfig = usePortalMutation({
    mutationFn: (newConfig: Partial<InstallationConfigPUTDto>) =>
      updateInstallationConfig(installationId, newConfig, authFetch).then(() => syncInstallation(installationId, authFetch)),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: installationConfigKey(installationId),
      }),
    onError: () => {
      enqueueErrorStack(t('common.update-failed-error'))
      setChargingPriority(currentChargingPriority)
    },
  })

  const updateChargingPriority = (newChargingPriority: ChargingPriorityType): void => {
    if (newChargingPriority === chargingPriority) return
    const isStorageFirst: boolean = newChargingPriority === 'Storage first'

    const newInstallationConfig: Partial<InstallationConfigPUTDto> = {
      emsBatteryBeforeCarMode: isStorageFirst,
      /** If storage gets priority, these values must be false. (Enforced by BE validation.)
       *  If battery gets priority, these values still need to be reset to false. */
      emsBatteryToCarMode: false,
      emsWallboxEnforcePowerAssignment: false,
    }

    setChargingPriority(newChargingPriority)
    mutateInstallationConfig.mutate(newInstallationConfig)
  }

  const isLoading = queryIsLoading || mutateInstallationConfig.isPending

  return {
    chargingPriority,
    wallboxOption,
    storageOption,
    updateChargingPriority,
    isLoading,
  }
}
